// Utility Rules
// Each utility has a defined number of analog and digital ports.
// Digital ports are referred to as "Feedback".
// Analog ports are represented as "Port" in the DMT profile.
// The DMT device provides a total of 4 digital ports and 2 analog ports.
// Utilities that require an electric network can share the same digital port.
// For example, an escalator and an elevator can both use a single digital port (network port).
// If, for example, a generator requires 3 digital ports and also uses the electric network,
// then it will occupy 2 individual digital ports, plus 1 shared electric network port (totaling 3).

import { t } from 'i18next';
import {
  AlarmOn,
  AlarmRunning,
  AlarmUtility, BatteryIcon, EletricNetworkIcon, Elevator, ElevatorEnergized, ElevatorOff, ElevatorRunning, Escalator, EscalatorEnergized, EscalatorOff, EscalatorRunning, Generator, GeneratorInFail, GeneratorInTest, GeneratorOff, GeneratorRunning, LightOffIcon, LightOnIcon, NobreakOffIcon, UtilityIcon, WaterBox,
  WaterTankFull,
} from '~/icons';

export const acessorUtilities = {
  Nobreak: {
    digitalPort: 1,
    analogicPort: 0,
    hasElectricNetwork: true,
    selectName: t('feedbackNobreak'),
    icon: <UtilityIcon />,
    states: {
      0: [t('desligadoMin')],
      1: [t('bateria')],
      2: [t('redeEletrica')],
    },
    ticks: [0, 1, 2],
    domain: [0, 2],
    namePt: t('nobreak'),
    namePlural: 'Nobreaks',
    usageIndex: {
      0: {
        label: t('tempoDesligado'),
        icon: <NobreakOffIcon />,
      },
      1: {
        label: t('tempoBateria'),
        icon: <BatteryIcon width="20" height="23" />,
      },
      2: {
        label: t('tempoRedeEletrica'),
        icon: <EletricNetworkIcon />,
      },
    },
  },
  Illumination: {
    digitalPort: 1,
    analogicPort: 0,
    hasElectricNetwork: true,
    selectName: t('feedbackIluminacao'),
    icon: <UtilityIcon />,
    states: {
      0: [t('desligadoMin')],
      1: [t('ligadoMin')],
    },
    ticks: [0, 1],
    domain: [0, 1],
    namePt: t('iluminacao'),
    namePlural: t('iluminacoes'),
    usageIndex: {
      0: {
        label: t('tempoDesligado'),
        icon: <LightOffIcon width="25" />,
      },
      1: {
        label: t('tempoLigado'),
        icon: <LightOnIcon width="25" />,
      },
    },
  },
  Elevator: {
    digitalPort: 1,
    analogicPort: 1,
    hasElectricNetwork: true,
    selectName: t('porta'),
    icon: <Elevator />,
    states: {
      0: [t('desligadoMin')],
      1: [t('energizada')],
      2: [t('emMaiusculo'), t('funcionamento')],
    },
    ticks: [0, 1, 2],
    domain: [0, 2],
    namePt: t('elevador'),
    namePlural: t('elevadores'),
    usageIndex: {
      0: {
        label: t('tempoDesligado'),
        icon: <ElevatorOff width="25" height="25" />,
      },
      1: {
        label: t('tempoEnergizada'),
        icon: <ElevatorEnergized width="25" height="25" />,
      },
      2: {
        label: t('tempoFuncionamento'),
        icon: <ElevatorRunning width="25" height="25" />,
      },
    },
  },
  Escalator: {
    digitalPort: 1,
    analogicPort: 1,
    hasElectricNetwork: true,
    selectName: t('porta'),
    icon: <Escalator />,
    states: {
      0: [t('desligadoMin')],
      1: [t('energizada')],
      2: [t('emMaiusculo'), t('funcionamento')],
    },
    ticks: [0, 1, 2],
    domain: [0, 2],
    namePt: t('escadaRolante'),
    namePlural: t('escadasRolantes'),
    usageIndex: {
      0: {
        label: t('tempoDesligado'),
        icon: <EscalatorOff width="25" height="25" />,
      },
      1: {
        label: t('tempoEnergizada'),
        icon: <EscalatorEnergized width="25" height="25" />,
      },
      2: {
        label: t('tempoFuncionamento'),
        icon: <EscalatorRunning width="25" height="25" />,
      },
    },
  },
  Generator: {
    digitalPort: 3,
    analogicPort: 0,
    hasElectricNetwork: true,
    selectName: t('feedbackGerador'),
    icon: <Generator />,
    states: {
      '-1': [t('emFalha')],
      0: [t('desligadoMin')],
      1: [t('emTeste')],
      2: [t('emMaiusculo'), t('funcionamento')],
    },
    ticks: [-1, 0, 1, 2],
    domain: [-1, 2],
    namePt: t('gerador'),
    namePlural: t('geradores'),
    usageIndex: {
      0: {
        label: t('tempoFuncionamento'),
        icon: <GeneratorRunning width="25" height="25" />,
      },
      1: {
        label: t('tempoTeste'),
        icon: <GeneratorInTest width="27" height="27" />,
      },
      2: {
        label: t('tempoDesligado'),
        icon: <GeneratorOff width="25" height="25" />,
      },
      '-1': {
        label: t('tempoFalha'),
        icon: <GeneratorInFail width="25" height="25" />,
      },
    },
  },
  'Intrusion Alarm': {
    digitalPort: 1,
    analogicPort: 0,
    hasElectricNetwork: false,
    selectName: t('feedbackAlarme'),
    icon: <AlarmUtility />,
    states: {
      0: [t('naoEstaEm'), t('alarme')],
      1: [t('emAlarme')],
    },
    ticks: [0, 1],
    domain: [0, 1],
    namePt: t('alarmeIntrusao'),
    namePlural: t('alarmesIntrusao'),
    usageIndex: {
      0: {
        label: t('tempoForaAlarme'),
        icon: <AlarmOn width="25" height="25" />,
      },
      1: {
        label: t('tempoEmAlarme'),
        icon: <AlarmRunning width="25" height="25" />,
      },
    },
  },
  'Fire Alarm': {
    digitalPort: 1,
    analogicPort: 0,
    hasElectricNetwork: false,
    selectName: t('feedbackAlarme'),
    icon: <AlarmUtility />,
    states: {
      0: [t('naoEstaEm'), t('alarme')],
      1: [t('emAlarme')],
    },
    ticks: [0, 1],
    domain: [0, 1],
    namePt: t('alarmeIncendio'),
    namePlural: t('alarmesIncendio'),
    usageIndex: {
      0: {
        label: t('tempoForaAlarme'),
        icon: <AlarmOn width="25" height="25" />,
      },
      1: {
        label: t('tempoEmAlarme'),
        icon: <AlarmRunning width="25" height="25" />,
      },
    },
  },
  'Water Box': {
    digitalPort: 1,
    analogicPort: 0,
    hasElectricNetwork: false,
    selectName: t('feedbackBoia'),
    icon: <WaterBox width="25" height="25" />,
    states: {
      1: [t('nivelDeAgua'), t('alto')],
      0: [t('nivelDeAgua'), t('baixo')],
    },
    ticks: [0, 1],
    domain: [0, 1],
    namePt: t('caixaDagua'),
    namePlural: t('caixasDagua'),
    usageIndex: {
      1: {
        label: t('tempoNivelAlto'),
        icon: <WaterTankFull width="25" height="25" />,
      },
      0: {
        label: t('tempoNivelBaixo'),
        icon: <WaterBox width="25" height="25" />,
      },
    },
  },
  'Electric Network': {
    digitalPort: 1,
    analogicPort: 0,
    hasElectricNetwork: false,
    selectName: t('feedbackRede'),
    icon: <UtilityIcon />,
    states: {
      0: [t('desligadoMin')],
      1: [t('ligadoMin')],
    },
    ticks: [0, 1],
    domain: [0, 1],
    namePt: t('redeEletrica'),
    namePlural: t('redesEletricas'),
    usageIndex: {
    },
  },
};

export const configExtraFields = {
  Nobreak: {
    idKey: 'UTILITY_ID',
    extraFields: (item: any) => ({ datCode: item.DAT_CODE }),
  },
  Illumination: {
    idKey: 'ILLUMINATION_ID',
    extraFields: (item: any) => ({ utilityId: item.UTILITY_ID }),
  },
  Escalator: {
    idKey: 'UTILITY_ID',
    extraFields: () => ({}),
  },
  Elevator: {
    idKey: 'UTILITY_ID',
    extraFields: () => ({}),
  },
  Generator: {
    idKey: 'UTILITY_ID',
    extraFields: () => ({}),
  },
  'Fire Alarm': {
    idKey: 'UTILITY_ID',
    extraFields: () => ({}),
  },
  'Intrusion Alarm': {
    idKey: 'UTILITY_ID',
    extraFields: () => ({}),
  },
  'Water Box': {
    idKey: 'UTILITY_ID',
    extraFields: () => ({}),
  },
};

export enum typesUtilities {
  'illumination' = 1,
  'nobreak' = 2,
  'elevator' = 3,
  'escalator' = 4,
  'generator' = 5,
  'firealarm' = 6,
  'intrusionalarm' = 7,
  'waterbox' = 8,
}

export const optionsDmt2 = [
  { name: t('nobreak'), value: 'Nobreak' },
  { name: t('iluminacao'), value: 'Illumination' },
  { name: t('escadaRolante'), value: 'Escalator' },
  { name: t('elevador'), value: 'Elevator' },
  { name: t('gerador'), value: 'Generator' },
  { name: t('alarmeIncendio'), value: 'Fire Alarm' },
  { name: t('alarmeIntrusao'), value: 'Intrusion Alarm' },
  { name: t('caixaDagua'), value: 'Water Box' },
];

export const optionsDmt = [
  { name: t('nobreak'), value: 'Nobreak' },
  { name: t('iluminacao'), value: 'Illumination' },
];

export const typesUtilitiesExistent = ['nobreak', 'elevator', 'escalator', 'intrusionalarm', 'firealarm', 'waterbox', 'generator'];

enum NobreakStatus {
  'Rede Eletrica' = 2,
  Bateria = 1,
  Desligado = 0,
}

enum GeneratorStatus {
  'Em falha' = -1,
  Desligado = 0,
  'Em teste' = 1,
  'Em funcionamento' = 2,
}

enum EscalatorElevatorStatus {
  Energizada = 1,
  'Em funcionamento' = 2,
  Desligado = 0,
}

export function treatNobreak(portValue: boolean, electricNetwork: boolean) {
  if (portValue && electricNetwork) {
    return NobreakStatus['Rede Eletrica'];
  }
  if (portValue && !electricNetwork) {
    return NobreakStatus.Bateria;
  }
  if (!electricNetwork && !portValue) {
    return NobreakStatus.Desligado;
  }
  return null;
}

export function getAnalogicPort(value: string, telemetry) {
  if (value === 'P0') return telemetry.C1;
  if (value === 'P1') return telemetry.C2;
}

export function treatTelemetryElevatorAndEscalator(portValue: number, electricNetwork: boolean) {
  if (!electricNetwork) {
    return EscalatorElevatorStatus.Desligado;
  }
  if (portValue <= 2 && electricNetwork) {
    return EscalatorElevatorStatus.Energizada;
  }
  if (portValue > 2 && electricNetwork) {
    return EscalatorElevatorStatus['Em funcionamento'];
  }
  return null;
}

export function treatGenerator(portValue1: boolean, portValue2: boolean, electricNetwork: boolean) {
  if ([portValue1, portValue2].every((value) => !value) && !electricNetwork) {
    return GeneratorStatus['Em falha'];
  }
  if (!portValue1 && portValue2 && electricNetwork) {
    return GeneratorStatus.Desligado;
  }
  if ([portValue1, portValue2].every((value) => value) && electricNetwork) {
    return GeneratorStatus['Em teste'];
  }
  if (portValue1 && portValue2 && !electricNetwork) {
    return GeneratorStatus['Em funcionamento'];
  }
  return null;
}
